import React, { useEffect, useMemo } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import logging from './config/logging';
import { protectedRoutes, routes } from './config/routes';
import MenuItemsLoader from './components/loading/menuLoader';
import { selectConfig, setConfigThunk } from './app/configSlice';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { ProtectedRoute } from './components/protectedRoutes';
import { getUserSettings } from './api/userSettings';
import { UserDto } from './api-types';
import { StatusPage } from './pages/status';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { setUserSettings } from './app/userSlice';
import { getAzureADUserToken } from './config/aws';
import { LoginType } from './api-types/models/LoginDto';
import { resetUserData, selectAuthUser } from './app/authSlice';
import { ObserverPage } from './pages/observer';
import { getUserGroups } from './utils/getUserGroups';
import { clearAppStorage } from './utils/clearAppStorage';
import { Toast } from './components/toast';
import { selectError } from './app/commonSlice';
import i18n from 'i18n';

import './App.css';

const App: React.FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const config = useAppSelector(selectConfig);
    const user = useAppSelector(selectAuthUser);
    const error = useAppSelector(selectError);

    const { instance, accounts, inProgress } = useMsal();
    const loginType = localStorage.getItem('login');
    const language = localStorage.getItem('lang');

    const publicPathnameArray = useMemo(
        () => routes.map((route) => route.path),
        [routes]
    );

    // Initialize config at the beginning
    useEffect(() => {
        // Wait for MSAL to finish any in-progress interactions (e.g. redirect
        // handling) before making authentication decisions
        if (inProgress !== InteractionStatus.None) {
            return;
        }

        if (!['/status', '/observer'].includes(location.pathname)) {
            logging.info('Loading application.');

            // Check if current route requires authentication
            const requiresAuth = !publicPathnameArray.includes(
                location.pathname
            );

            // Set config immediately for non-SPORTRADAR login types
            if (
                loginType === LoginType.BRUNCH ||
                loginType === LoginType.COACH
            ) {
                // For Cognito users, set config without tokens immediately
                dispatch(setConfigThunk(undefined));
            } else if (!loginType && !requiresAuth) {
                // No login type but accessing public route, set config immediately
                dispatch(setConfigThunk(undefined));
            } else if (loginType === LoginType.SPORTRADAR && !accounts.length) {
                // SPORTRADAR login type set but no active accounts yet (user needs to log in)
                dispatch(setConfigThunk(undefined));
            }

            // Handle SPORTRADAR login asynchronously
            if (loginType === LoginType.SPORTRADAR && accounts.length) {
                getAzureADUserToken(instance, accounts)
                    .then((token) => {
                        if (token?.idToken && token?.accessToken) {
                            const { idToken, accessToken } = token;
                            dispatch(setConfigThunk({ idToken, accessToken }));
                        } else if (requiresAuth) {
                            // Token is invalid, redirect to login
                            logging.warn(
                                'Invalid or missing Azure AD token, redirecting to login'
                            );
                            clearAppStorage();
                            navigate('/login');
                        }
                    })
                    .catch((err) => {
                        logging.error(`Error fetching Azure AD token: ${err}`);
                        if (requiresAuth) {
                            navigate('/login');
                        }
                    });
            } else if (requiresAuth && !loginType) {
                // No login type set and trying to access protected route
                logging.warn('No login type found, redirecting to login');
                navigate('/login');
            }
        }
    }, [instance, accounts, loginType, inProgress]);

    useEffect(() => {
        const isUser =
            user &&
            !getUserGroups(user).includes(LoginType.BRUNCH) &&
            !getUserGroups(user).includes(LoginType.COACH);

        // Only fetch user settings if we have config and proper authentication
        if (
            (config && isUser) ||
            (config && loginType === LoginType.SPORTRADAR)
        ) {
            getUserSettings()
                .then((response: UserDto) => {
                    if (!response['error']) {
                        dispatch(setUserSettings(response));
                    } else {
                        logging.warn(
                            `Error fetching user settings: ${JSON.stringify(response)}`
                        );
                        if (response['error'] === 401) {
                            // User is not authenticated, clear session and redirect
                            if (
                                localStorage.getItem('login') ===
                                LoginType.SPORTRADAR
                            ) {
                                clearAppStorage();
                                instance
                                    .logoutPopup({
                                        postLogoutRedirectUri: '/'
                                    })
                                    .then(() => navigate('/login'));
                            } else {
                                clearAppStorage();
                                dispatch(resetUserData());
                                navigate('/login');
                            }
                        }
                    }
                })
                .catch((error) => {
                    logging.error(
                        `Failed to fetch user settings: ${JSON.stringify(error)}`
                    );
                    // On any error, check if it's auth-related and redirect
                    if (
                        error?.error === 401 ||
                        error?.response?.status === 401
                    ) {
                        clearAppStorage();
                        dispatch(resetUserData());
                        navigate('/login');
                    }
                });
        }
    }, [config, user]);

    useEffect(() => {
        if (language) {
            i18n.changeLanguage(language);
        }
    }, [language]);

    if (location.pathname === '/status') {
        return <StatusPage />;
    }

    if (location.pathname === '/observer') {
        return <ObserverPage />;
    }

    if (!config && !publicPathnameArray.includes(location.pathname)) {
        return <MenuItemsLoader />;
    }

    return (
        <div className='kitchen-wrapper'>
            <Routes>
                {routes.map((route) => {
                    return (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={<route.component name={route.name} />}
                        />
                    );
                })}
                {protectedRoutes.map((route) => {
                    return (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={<ProtectedRoute route={route} />}
                        />
                    );
                })}
            </Routes>
            {error && <Toast message={error.message} />}
        </div>
    );
};

export default App;
